import clsx from 'clsx';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { THEME_COLORS, type ThemeColor } from '../../types/theme';
import Switch from '../primitives/Switch';
import OptionToggle from './OptionToggle';

interface ColorPickerProps {
  currentColor: ThemeColor;
  useSystemColor: boolean;
  onColorChange: (color: ThemeColor) => void;
  onSystemColorChange: (checked: boolean) => void;
  className?: string;
  /** ARIA role for each color button (e.g. "menuitem" when used inside a menu) */
  itemRole?: string;
  /** The left-hand menu uses a switch. The reader menus use an On/Off button. */
  toggle?: 'button' | 'switch';
}

function ColorPicker({
  currentColor,
  useSystemColor,
  onColorChange,
  onSystemColorChange,
  className,
  itemRole,
  toggle = 'button',
}: ColorPickerProps) {
  return (
    <div className={clsx('flex flex-col gap-3', className)}>
      <div className="color-picker-swatches">
        {THEME_COLORS.map((themeColor) => (
          <button
            key={themeColor}
            onClick={() => onColorChange(themeColor)}
            className={clsx(
              'color-picker-swatch duration-normal',
              currentColor === themeColor && 'color-picker-swatch-selected',
            )}
            style={{
              backgroundColor:
                themeColor === 'light'
                  ? 'var(--white-100)'
                  : themeColor === 'sepia'
                    ? 'var(--sepia-100)'
                    : themeColor === 'storm'
                      ? 'var(--neutral-800)'
                      : 'var(--neutral-900)',
              borderColor:
                currentColor === themeColor ? '#0074bf' : 'var(--border-strong)',
            }}
            role={itemRole}
            aria-label={t`${themeColor} theme`}
          />
        ))}
      </div>
      <div
        className={
          toggle === 'switch'
            ? 'flex w-full items-center justify-between gap-1'
            : 'read-option-row'
        }
      >
        <span className="text-sm">
          <Trans>Auto Dark Mode</Trans>
        </span>
        {toggle === 'switch' ? (
          <Switch
            checked={useSystemColor}
            onChange={onSystemColorChange}
            aria-label={t`Toggle system default theme`}
          />
        ) : (
          <OptionToggle
            checked={useSystemColor}
            onChange={onSystemColorChange}
            aria-label={t`Toggle system default theme`}
          />
        )}
      </div>
    </div>
  );
}

export default ColorPicker;
